// src/helpers/workshop.js
import { getData, postData, putData } from './ApiSettings';

export const WorkshopManager = {
  // Fetch all available workshops
  fetchWorkshops: async (filters = {}) => {
    try {
      let queryParams = 'populate=*';
      
      // Add filters if any
      if (filters.category) {
        queryParams += `&filters[category][id][$eq]=${filters.category}`;
      }
      
      if (filters.upcoming === true) {
        const today = new Date().toISOString();
        queryParams += `&filters[dates][date][$gte]=${today}`;
      }
      
      if (filters.reference) {
        queryParams += `&filters[reference][$eq]=${filters.reference}`;
      }

      const response = await getData(`/workshops?${queryParams}`);
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching workshops:', error);
      throw error;
    }
  },

  // Fetch a specific workshop by ID
  fetchWorkshopById: async (workshopId) => {
    try {
      const response = await getData(`/workshops/${workshopId}?populate=*`);
      return response.data.data;
    } catch (error) {
      console.error(`Error fetching workshop with ID ${workshopId}:`, error);
      throw error;
    }
  },

  // Fetch workshop dates for a specific workshop
  fetchWorkshopDates: async (workshopId) => {
    try {
      const response = await getData(`/workshop-dates?filters[workshop][documentId][$eq]=${workshopId}&populate=*&sort=date:asc`);
    //   console.log(response.data)
      return response.data.data || [];
    } catch (error) {
      console.error(`Error fetching dates for workshop ID ${workshopId}:`, error);
      throw error;
    }
  },

  // Fetch workshop categories
  fetchWorkshopCategories: async () => {
    try {
      const response = await getData('/workshop-categories');
      return response.data.data || [];
    } catch (error) {
      console.error('Error fetching workshop categories:', error);
      throw error;
    }
  },

  // Check availability for a workshop date
  checkAvailability: async (dateId) => {
    try {
      const response = await getData(`/workshop-dates/${dateId}?populate=*`);
      const dateData = response.data.data;
      
      if (!dateData) {
        return { available: false, message: 'Date non trouvée' };
      }
      
      // Get current and max participants
      const maxParticipants = dateData.max_participants || 12;
      const currentParticipants = dateData.current_participants || 0;
      
      return {
        available: currentParticipants < maxParticipants,
        remainingSpots: maxParticipants - currentParticipants,
        maxParticipants,
        currentParticipants
      };
    } catch (error) {
      console.error(`Error checking availability for date ID ${dateId}:`, error);
      return { available: false, message: 'Erreur lors de la vérification de disponibilité' };
    }
  },

  // Create a workshop reservation
  createReservation: async (reservationData) => {
    try {
      const { userId, workshopId, dateId, totalPrice, userDetails } = reservationData;
      
      // Check availability first
      const availability = await WorkshopManager.checkAvailability(dateId);
      if (!availability.available) {
        throw new Error('Cet atelier est complet. Veuillez choisir une autre date.');
      }
      
      // Create the reservation
      const response = await postData('/workshop-reservations', {
        data: {
          user: userId,
          workshop: workshopId,
          workshop_date: dateId,
          total_price: totalPrice,
          resa_status: 'pending_payment',
          user_details: userDetails
        }
      });
      
      if (!response.data?.data) {
        throw new Error('Échec de la création de la réservation');
      }
      
      return {
        success: true,
        reservationId: response.data.data.id,
        documentId: response.data.data.documentId
      };
    } catch (error) {
      console.error('Error creating workshop reservation:', error);
      return {
        success: false,
        error: error.message || 'Échec de la création de la réservation'
      };
    }
  },

  // Update a reservation
  updateReservation: async (reservationId, data) => {
    try {
      const response = await putData(`/workshop-reservations/${reservationId}`, {
        data
      });
      return response.data.data;
    } catch (error) {
      console.error(`Error updating reservation ${reservationId}:`, error);
      throw error;
    }
  },
  
  // Update a workshop date
  updateWorkshopDate: async (dateId, data) => {
    try {
      const response = await putData(`/workshop-dates/${dateId}`, {
        data
      });
      return response.data.data;
    } catch (error) {
      console.error(`Error updating workshop date ${dateId}:`, error);
      throw error;
    }
  },

  // Get reservation by document ID
  getReservationByDocumentId: async (documentId) => {
    try {
      const response = await getData(`/workshop-reservations?filters[documentId][$eq]=${documentId}&populate=*`);
      
      if (!response.data?.data || response.data.data.length === 0) {
        throw new Error('Réservation non trouvée');
      }
      
      return response.data.data[0];
    } catch (error) {
      console.error(`Error fetching reservation with document ID ${documentId}:`, error);
      throw error;
    }
  },

  // Get user's workshop reservations
  getUserReservations: async (userId) => {
    try {
      const response = await getData(`/workshop-reservations?filters[user][id][$eq]=${userId}&populate=*&sort=createdAt:desc`);
      return response.data.data || [];
    } catch (error) {
      console.error(`Error fetching reservations for user ${userId}:`, error);
      throw error;
    }
  }
};

export default WorkshopManager;